import AddIcon from '@mui/icons-material/Add'
import StyledCard from 'components/UI/StyledCard'
import EmptyStateSection from '../LandingPages/ui/LandingPageEditor/RulesPage/EmptyStateSection'
import { useCallback, useEffect, useMemo, useReducer } from 'react'
import { useBookends } from 'lib/BookendsProvider'
import { ApolloQueryResult, useMutation, useSuspenseQuery } from '@apollo/client'
import { useCurrentUser } from 'lib/CurrentUserProvider'
import { CREATE_DISTRIBUTION_LIST_MUTATION, DESTROY_DISTRIBUTION_LIST_MUTATION, GET_DISTRIBUTION_LISTS_QUERY, UPDATE_DISTRIBUTION_LIST_MUTATION } from './queries'
import COLORS from 'lib/colors';
import { FormTextField, getIn, useFormikContext } from 'components/Formik/forms'
import StandardDialog from 'components/UI/Dialog/StandardDialog'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { Chip, Stack } from '@mui/material'
import capitalize from 'lodash-es/capitalize'
import startCase from 'lodash-es/startCase'
import FormWrapper from 'components/Formik/FormWrapper'
import * as yup from 'yup';
import FreeTextMultiSelect from 'components/UI/FreeTextMultiSelect/FreeTextMultiSelect'
import omit from 'lodash-es/omit'
import { useNotifications } from 'lib/NotificationsProvider'
import { isBlank } from 'lib/utils'
import { Exact, InputMaybe, Scalars, GetDistributionListsQuery } from 'generated/graphql'
import { useCloseOnMissingList } from './useCloseOnMissingList'
import GroupTable, { GroupRow } from './GroupTable'


export const distributionListSchema = yup.object({
  id: yup.string().optional(),
  name: yup.string().trim().required('Name is required'),
  description: yup.string().trim().optional(),
  recipients: yup
    .array()
    .of(
      yup
        .string()
        .trim()
        .email('Recipient must be a valid email address')
        .required('Recipient is required'),
    )
    .min(1, 'At least one recipient is required')
    .required('Recipients are required'),
})

const MISSING_LIST_WARNING = 'That group could not be found, please refresh page.'

const ADD = 'add' as const
const EDIT = 'edit' as const
const DUPLICATE = 'duplicate' as const

const VERB_BY_MODE: Record<Mode, string> = {
  [ADD]: 'Add New',
  [EDIT]: 'Edit',
  [DUPLICATE]: 'Duplicate',
}

const isEdit = (mode) => mode === EDIT
const isAdd = (mode) => mode === ADD

export type Mode = typeof ADD | typeof EDIT | typeof DUPLICATE

type CreatedBy = {
  id: string
  name: string
  email: string
}

export type DistributionList = {
  id: string
  name: string
  usedInCount?: number | null
  description?: string | null
  recipients: string[]
  updatedAt: string
  createdBy?: CreatedBy | null
};

type GroupId = string

type Modal =
  | { type: 'none' }
  | { type: 'view'; groupId: GroupId }
  | { type: 'form'; mode: Mode; groupId?: GroupId }
  | { type: 'deleteConfirm'; groupId: GroupId }

type ModalState = {
  current: Modal
  returnTo: Modal | null // used only for the special paths
}

export type Action =
  | { type: 'OPEN_ADD' }
  | { type: 'OPEN_VIEW'; groupId: GroupId }
  | { type: 'OPEN_EDIT'; groupId: GroupId }
  | { type: 'OPEN_DUPLICATE'; groupId: GroupId }
  | { type: 'OPEN_DELETE'; groupId: GroupId }

  // special-path transitions:
  | { type: 'VIEW_TO_EDIT' }
  | { type: 'EDIT_TO_DELETE' }

  // close behaviors:
  | { type: 'CLOSE_MODAL' }
  | { type: 'RESET_RETURN' }

const initialReducerState: ModalState = {
  current: { type: 'none' },
  returnTo: null,
}

function reducer(state: ModalState, action: Action): ModalState {
  switch (action.type) {
    // ----- normal opens (no return) -----
    case 'OPEN_ADD':
      return { current: { type: 'form', mode: ADD }, returnTo: null }

    case 'OPEN_VIEW':
      return { current: { type: 'view', groupId: action.groupId }, returnTo: null }

    case 'OPEN_EDIT':
      return { current: { type: 'form', mode: EDIT, groupId: action.groupId }, returnTo: null }

    case 'OPEN_DUPLICATE':
      return { current: { type: 'form', mode: DUPLICATE, groupId: action.groupId }, returnTo: null }

    case 'OPEN_DELETE':
      return { current: { type: 'deleteConfirm', groupId: action.groupId }, returnTo: null }

    // ----- special path: View -> Edit (return to View on close) -----
    case 'VIEW_TO_EDIT': {
      if (state.current.type !== 'view') return state
      const { groupId } = state.current
      return {
        current: { type: 'form', mode: EDIT, groupId },
        returnTo: state.current, // return to view
      };
    }

    // ----- special path: Edit -> Delete (return to Edit on close) -----
    case 'EDIT_TO_DELETE': {
      if (state.current.type !== 'form' || state.current.mode !== EDIT || !state.current.groupId) return state
      const groupId = state.current.groupId
      return {
        current: { type: 'deleteConfirm', groupId },
        returnTo: state.current, // return to edit
      }
    }

    // ----- close: pop back if returnTo exists -----
    case 'CLOSE_MODAL': {
      if (state.returnTo) {
        return { current: state.returnTo, returnTo: null }
      }
      return { current: { type: 'none' }, returnTo: null }
    }

    case 'RESET_RETURN':
      return { ...state, returnTo: null }

    default:
      return state
  }
}

type FormValues = {
  id?: string
  name: string
  description: string
  recipients: string[]
}

const emptyValues: FormValues = { name: '', description: '', recipients: [] }

function assertList(
  list: DistributionList | undefined,
  mode: Mode,
): asserts list is DistributionList {
  if (!list) throw new Error(`Missing list for mode: ${mode}`)
}

function buildDistributionListFormValues(mode: Mode, list?: DistributionList): FormValues {
  if (mode === ADD) return emptyValues

  assertList(list, mode)

  switch (mode) {
    case EDIT:
      return {
        id: list.id,
        name: list.name,
        description: list.description ?? '',
        recipients: list.recipients,
      }

    case DUPLICATE:
      return {
        name: `${list.name} (copy)`,
        description: list.description ?? '',
        recipients: list.recipients,
      }

    default: {
      const _exhaustive: never = mode
      throw new Error(`Unhandled mode: ${_exhaustive}`)
    }
  }
}

function AddEditDuplicateGroupForm() {
  const { values, setFieldValue, errors, setErrors } = useFormikContext<DistributionList>()

  return (
    <Stack direction='column' gap={2} mt={1}>
      <FormTextField
        label='Group Name'
        name='name'
        required
        sx={{ pr: 7 }}
      />
      <FormTextField
        label='Description'
        name='description'
        sx={{ pr: 7 }}
      />
      <FreeTextMultiSelect
        label='Member Email(s)'
        value={values.recipients}
        setValue={(newValue) => setFieldValue('recipients', newValue)}
        required={true}
        error={!!getIn(errors, 'recipients')}
        onFocus={() => setErrors(omit(errors, ['recipients']))}
        sx={{ pr: 7 }}
      />
    </Stack>
  )
}

type AddEditDuplicateGroupDialogProps = {
  mode: Mode
  dispatch: React.Dispatch<Action>
}

function AddEditDuplicateGroupDialog({ mode, dispatch }: AddEditDuplicateGroupDialogProps) {
  const { values, submitForm, isSubmitting, dirty } = useFormikContext<DistributionList>()

  const deleteButton = (
    <Box width='100%'>
      <Button
        variant='outlined'
        onClick={() => dispatch({ type: 'EDIT_TO_DELETE' })}
        sx={{ color: COLORS.frenchBlue, borderColor: COLORS.beauBlue }}
      >
        Delete
      </Button>
    </Box>
  )

  return (
    <StandardDialog
      title={`${VERB_BY_MODE[mode]} Group`}
      description={isEdit(mode) ? "Changes to groups will apply everywhere they're used.": undefined}
      confirmText='Save'
      onConfirm={() => submitForm()}
      onCancel={() => dispatch({ type: 'CLOSE_MODAL' })}
      disabled={isBlank(values.name) || isBlank(values.recipients) || (isEdit(mode) && !dirty)}
      loading={isSubmitting}
      maxWidth='600px'
      footer={isEdit(mode) ? deleteButton : undefined}
    >
      <AddEditDuplicateGroupForm />
    </StandardDialog>
  )
}

type AddEditDuplicateGroupContainerProps = {
  list?: DistributionList
  mode: Mode
  refetch: (variables?: Partial<Exact<{userId?: InputMaybe<Scalars["ID"]["input"]>}>> | undefined) => Promise<ApolloQueryResult<GetDistributionListsQuery>>
  dispatch: React.Dispatch<Action>
}

function AddEditDuplicateGroupContainer({ list, mode, refetch, dispatch }: AddEditDuplicateGroupContainerProps) {
  const { currentUser, currentOrganization } = useCurrentUser()
  const { addNotification } = useNotifications()

  const close = useCallback(() => dispatch({ type: 'CLOSE_MODAL' }), [dispatch])

  useCloseOnMissingList(list, addNotification, {
    shouldRequire: !isAdd(mode),
    missingMessage: MISSING_LIST_WARNING,
    close,
  })

  const [gqlCreate] = useMutation(CREATE_DISTRIBUTION_LIST_MUTATION)
  const [gqlUpdate] = useMutation(UPDATE_DISTRIBUTION_LIST_MUTATION)

  const handleCreate = useCallback((values, { setSubmitting }) => {
    setSubmitting(true)
    const mutation = {
      name: values.name,
      description: values.description,
      recipients: values.recipients,
      createdById: currentUser.id,
      organizationId: currentOrganization.id
    }

    gqlCreate({ variables: mutation }).then(() => {
      addNotification({variant: 'notice', message: `Created "${values.name}" group.`})
      dispatch({ type: 'CLOSE_MODAL' })
      refetch()
    }).catch(() => {
      addNotification({variant: 'alert', message: `Trouble creating "${values.name}" group. Please try again later.` })
    }).finally(() => {
      setSubmitting(false)
    })
  }, [currentUser.id, currentOrganization.id, gqlCreate, addNotification, refetch, dispatch])

  const handleUpdate = useCallback((values, { setSubmitting }) => {
    setSubmitting(true)
    const mutation = {
      id: values.id,
      name: values.name,
      description: values.description,
      recipients: values.recipients,
    }

    gqlUpdate({ variables: mutation }).then(() => {
      addNotification({variant: 'notice', message: `Updated "${values.name}" group.`})
      dispatch({ type: 'CLOSE_MODAL' })
      refetch()
    }).catch(() => {
      addNotification({variant: 'alert', message: `Trouble updating "${values.name}" group. Please try again later.` })
    }).finally(() => {
      setSubmitting(false)
    })
  }, [gqlUpdate, addNotification, refetch, dispatch])

  const onConfirmByMode = {
    [ADD]: handleCreate,
    [EDIT]: handleUpdate,
    [DUPLICATE]: handleCreate,
  } as const

  if (!isAdd(mode) && !list) return null

  const data = buildDistributionListFormValues(mode, list)

  return (
    <FormWrapper data={data} schema={distributionListSchema} onSubmit={onConfirmByMode[mode]}>
      <AddEditDuplicateGroupDialog mode={mode} dispatch={dispatch} />
    </FormWrapper>
  )
}

type DeleteGroupConfirmationDialogProps = {
  list?: DistributionList
  refetch: (variables?: Partial<Exact<{userId?: InputMaybe<Scalars["ID"]["input"]>}>> | undefined) => Promise<ApolloQueryResult<GetDistributionListsQuery>>
  dispatch: React.Dispatch<Action>
}

function DeleteGroupConfirmationDialog({ list, refetch, dispatch }: DeleteGroupConfirmationDialogProps) {
  const { addNotification } = useNotifications()
  const [gqlDestroy] = useMutation(DESTROY_DISTRIBUTION_LIST_MUTATION)

  const resetAndClose = useCallback(() => { dispatch({ type: 'RESET_RETURN' }); dispatch({ type: 'CLOSE_MODAL' }) }, [dispatch])

  useCloseOnMissingList(list, addNotification, {
    shouldRequire: true,
    missingMessage: MISSING_LIST_WARNING,
    close: resetAndClose,
  })

  const handleDelete = useCallback((id, name) => {
    const mutation = {
      id: id,
    }

    gqlDestroy({ variables: mutation }).then(() => {
      addNotification({variant: 'notice', message: `Deleted "${name}" group.`})
      dispatch({ type: 'RESET_RETURN' })
      dispatch({ type: 'CLOSE_MODAL' })
      refetch()
    }).catch(() => {
      addNotification({variant: 'alert', message: `Trouble deleting "${name}" group. Please try again later.` })
    })
  }, [gqlDestroy, addNotification, refetch, dispatch])

  if (!list) return null

  return (
    <StandardDialog
      title={'Delete Group'}
      description={<>Do you want to delete the group <strong>{list.name}</strong></>}
      confirmText='Delete'
      onConfirm={() => handleDelete(list.id, list.name)}
      onCancel={resetAndClose}
      maxWidth='600px'
    />
  )
}

type ViewGroupDialogProps = {
  list?: DistributionList
  dispatch: React.Dispatch<Action>
}

function ViewGroupDialog({ list, dispatch }: ViewGroupDialogProps) {
  const { addNotification } = useNotifications()
  const resetAndClose = useCallback(() => { dispatch({ type: 'RESET_RETURN' }); dispatch({ type: 'CLOSE_MODAL' }) }, [dispatch])

  useCloseOnMissingList(list, addNotification, {
    shouldRequire: true,
    missingMessage: MISSING_LIST_WARNING,
    close: resetAndClose,
  })

  if (!list) return null

  const description = (
    <>
      {list.description && (
        <Typography variant='modal' pb={2}>
          {capitalize(list.description)}
        </Typography>
      )}

      <Typography variant='h4' pb={1}>
        Member Emails
      </Typography>

      {list.recipients.map((email) => (
        <Chip label={email} key={email} sx={{ height: '28px', mr: 1, mb: 1 }} />
      ))}
    </>
  )

  const editButton = (
    <Box width='100%'>
      <Button
        variant='outlined'
        onClick={() => dispatch({ type: 'VIEW_TO_EDIT' })}
        sx={{ color: COLORS.frenchBlue, borderColor: COLORS.beauBlue }}
      >
        Edit Group
      </Button>
    </Box>
  )

  return (
    <StandardDialog
      title={startCase(list.name)}
      description={description}
      onClose={() => dispatch({ type: 'CLOSE_MODAL' })}
      maxWidth='600px'
      footer={editButton}
    />
  )
}

function AllGroupsSection({ distributionLists, dispatch }) {
  const { currentUser, currentOrganization } = useCurrentUser()

  const data = useMemo<GroupRow[]>(() => {
    return distributionLists.map(list => {
      const createdByName = (list.createdBy?.id === currentUser.id) ? 'Me' : list.createdBy?.name

      return {
        id: list.id,
        lastUpdated: list.updatedAt,
        createdByName: createdByName || getIn(currentOrganization, 'attributes.name'),
        name: list.name,
        description: list.description || '—',
        membersCount: list.recipients?.length,
        usedInCount: list.usedInCount,
        recipients: list.recipients,
      } as const
    })
  }, [currentOrganization, currentUser.id, distributionLists])

  return(
    <StyledCard
      title={'Group Lists'}
      note="These groups are shared with your whole organization. Changes to groups will apply everywhere they're used."
      controls={[{ icon: <AddIcon/>, onClick: () => dispatch({ type: 'OPEN_ADD' }), label: 'Add New Group' }]}
      isSubsection
      hasStickyHeader
      role="group"
    >
      {distributionLists.length > 0 &&
        <GroupTable data={data} dispatch={dispatch} />
      ||
        <EmptyStateSection message={'No groups added.'} />
      }
    </StyledCard>
  )
}

export default function Groups() {
  const [state, dispatch] = useReducer(reducer, initialReducerState);
  const { currentUser } = useCurrentUser()
  const { setBookends } = useBookends()

  useEffect(() => {
    setBookends({
      footer: {
        buttons: [
          {
            type: "primary",
            label: 'Add New Group',
            onClick: () => dispatch({ type: 'OPEN_ADD' }),
          },
        ],
      },
    })
  }, [setBookends])

  const { data: { distributionLists }, refetch } = useSuspenseQuery(GET_DISTRIBUTION_LISTS_QUERY, {
    variables: { userId: currentUser.id }, fetchPolicy: 'network-only'
  })

  const listsById = useMemo(() => {
    const map = new Map<string, DistributionList>()
    for (const l of distributionLists) map.set(l.id, l)
    return map
  }, [distributionLists])

  const getList = useCallback(
    (id?: string) => (id ? listsById.get(id) : undefined),
    [listsById],
  )

  const modal = state.current

  return (
    <>
      <AllGroupsSection distributionLists={distributionLists} dispatch={dispatch} />

      {modal.type === 'view' && (
        <ViewGroupDialog
          list={getList(modal.groupId)}
          dispatch={dispatch}
        />
      )}

      {modal.type === 'form' && (
        <AddEditDuplicateGroupContainer
          list={getList(modal.groupId)}
          mode={modal.mode}
          refetch={refetch}
          dispatch={dispatch}
        />
      )}

      {modal.type === 'deleteConfirm' && (
        <DeleteGroupConfirmationDialog
          list={getList(modal.groupId)}
          refetch={refetch}
          dispatch={dispatch}
        />
      )}
    </>
  )
}
